/** Per-photo upload attempts (initial + retries) and the exponential-backoff base. */
export const UPLOAD_MAX_ATTEMPTS = 3;
export const UPLOAD_RETRY_BASE_MS = 500;

export interface UploadUnsyncedImagesDeps<T> {
  /** Upload one image (a raw XHR that bypasses the request transport). */
  upload: (obs: T) => Promise<void>;
  /** Mark the observation synced once its image is received. */
  markSynced: (obs: T) => Promise<void>;
  /** Called after each photo (whether it succeeded or was given up on), 1-based. */
  onPhotoDone?: (loaded: number, total: number) => void;
  /** Called with the last error of a photo given up on after its retries. */
  onGaveUp?: (obs: T, error: unknown) => void;
  /** Backoff delay - real timers in prod, injectable (e.g. instant) in tests. */
  delay?: (ms: number) => Promise<void>;
}

const defaultDelay = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Upload each unsynced image with bounded per-photo retry + exponential backoff.
 *
 * Retries live here because the photo upload uses a raw XHR that bypasses the
 * request transport. Critically, a single failing photo must NOT abort the batch: a
 * flaky upload previously blocked every later photo until a full re-sync. A photo
 * that still fails after {@link UPLOAD_MAX_ATTEMPTS} is recorded in `failures` and
 * left unsynced (so it retries on the next sync) while the rest continue. Returns
 * per-run counts so the caller can decide "completed" vs "error".
 *
 * Pure and dependency-injected (no React / services / timers of its own) so the
 * retry policy is unit-testable in isolation.
 */
export async function uploadUnsyncedImages<
  T extends { externalId?: string; value?: string },
>(
  observations: T[],
  deps: UploadUnsyncedImagesDeps<T>,
): Promise<{ uploadedCount: number; failures: string[] }> {
  const delay = deps.delay ?? defaultDelay;
  const total = observations.length;
  let uploadedCount = 0;
  const failures: string[] = [];

  for (let i = 0; i < total; i++) {
    const obs = observations[i];
    let uploaded = false;
    let lastErr: unknown = null;

    for (
      let attempt = 0;
      attempt < UPLOAD_MAX_ATTEMPTS && !uploaded;
      attempt++
    ) {
      try {
        if (attempt > 0) {
          await delay(UPLOAD_RETRY_BASE_MS * 2 ** (attempt - 1));
        }
        await deps.upload(obs);
        await deps.markSynced(obs);
        uploaded = true;
      } catch (err) {
        lastErr = err;
      }
    }

    if (uploaded) {
      uploadedCount++;
    } else {
      console.error('Image upload failed after retries:', lastErr);
      failures.push(obs.externalId ?? obs.value ?? String(i));
      deps.onGaveUp?.(obs, lastErr);
    }

    deps.onPhotoDone?.(i + 1, total);
  }

  return { uploadedCount, failures };
}
